/* v8 ignore file -- @preserve */
import { viewTypes } from '#shared/constants';
import { logWarn } from '#shared/utils/Logger';
import { getHeaderFallback } from './XapiFallback';

//TODO
import { mobileTitles } from 'config';
import experiments from '#shared/features/experimentation/config';
import { localKillswitches } from '#shared/features/killswitches/config';
import { combineKillswitches } from '#shared/features/killswitches';
import transformEditorPopupLink from '~/utils/transformEditorPopupLink';
//TODO

function buildHeaderRequestUrl({ regionCode, currencyCode, deviceType, experimentIds, overrideCMParams }) {
  const baseParams = [
    '_ccsHeader=true',
    '_removeCmFromUrls=true',
    '_menuFromStella=true',
    '_navigationType=BROWSE',
    '_shoppingMode=SITE',
    '_getL3Response=true',
    `_deviceType=${deviceType}`,
    `_regionCode=${regionCode || 'US'}`,
    `_currencyCode=${currencyCode || 'USD'}`,
  ];

  if (overrideCMParams) {
    baseParams.push('_overrideCM=true');
  }
  if (experimentIds.length) {
    baseParams.push(`_customerExperiment=${experimentIds}`);
  }

  return `/xapi/navigate/v1/header?${baseParams.join('&')}`;
}

async function loadHeaderMock(params) {
  logWarn('[navigation] Failed to request header data from headerfooter-xapi, using mock data instead.');

  return (await getHeaderFallback(params)).value;
}

function getExperimentIdForMock({ experimentIds, viewType }) {
  const experimentIdsMap = experimentIds.reduce((acc, cur) => {
    acc[cur] = true;
    return acc;
  }, {});
  const experiment = experiments.find(
    ({ treatment, isHeader, mockable, devices }) => mockable && isHeader && experimentIdsMap[treatment] && devices.includes(viewType),
  );

  return experiment ? experiment.treatment : null;
}

function remove(links, textsToRemove) {
  const normalizedTexts = textsToRemove.map((text) => text.toLowerCase());
  return links.filter((link) => !normalizedTexts.includes(link.text?.toLowerCase()?.trim()));
}

function isExperimentActive(experimentIds, killswitches, expName, viewType) {
  const exp = experiments.find((i) => i.ks === expName) || null;

  if (exp) {
    const isExperimentEnabled = exp.scaled || (experimentIds.includes(exp.treatment) && killswitches[expName]);
    const isDeviceMatched = exp.devices.includes(viewType);

    return isExperimentEnabled && isDeviceMatched;
  }

  return false;
}

function getColorFromAttributes(attributes, prop) {
  if (!attributes) {
    return '';
  }

  const defaultColors = ['#000', '#000000'];
  const color = attributes.find(({ name }) => name === prop)?.value;

  if (!color) {
    return '';
  }

  return defaultColors.includes(color) ? '' : color;
}

function getFobColumnGroupData({ contentLinks: { value, text, semanticUrl, tracking } }, index, idPrefix, linkColor) {
  return {
    text,
    tracking,
    id: `${idPrefix}_${index}`, // we cannot use value as an ID because value can contain url instead of ID, and url can be not unique
    url: semanticUrl || value,
    color: linkColor,
  };
}

function getFobColumnData({ contents }, groupIndex, idPrefix) {
  return contents.map((column, index) => {
    const { flexibleLinks, navigationFlyoutAd } = column;
    const { attributes } = flexibleLinks || {};
    const subCategoryColor = getColorFromAttributes(attributes, 'categoryFontColor');
    const linkColor = getColorFromAttributes(attributes, 'subCategoryFontColor');
    const newIdPrefix = `${idPrefix}_${groupIndex}_${index}`;

    if (navigationFlyoutAd) {
      const adLink = navigationFlyoutAd.contentlinks?.[0];
      const imageLink = navigationFlyoutAd.image?.contentlinks?.[0];
      const url = adLink?.semanticUrl || adLink?.value || '';

      return {
        url,
        isImage: true,
        src: `${navigationFlyoutAd.urlTemplate}${navigationFlyoutAd.urlTemplate.endsWith('/') ? '' : '/'}${navigationFlyoutAd.image.fileName}`,
        imageLink: imageLink?.semanticUrl || imageLink?.value || '',
        imageLinkTracking: imageLink?.tracking || null,
        text: adLink?.text || '',
        alt: navigationFlyoutAd.body || adLink?.text,
        linkTracking: adLink?.tracking || null,
      };
    }

    const groupLink = flexibleLinks.category?.[0]?.contentLinks || {};

    groupLink.text = groupLink.text || '';
    groupLink.id = groupLink.value || newIdPrefix;

    const url = groupLink.semanticUrl || groupLink.value || '';

    return {
      url,
      id: groupLink.id,
      text: groupLink.text,
      tracking: url ? groupLink.tracking : null,
      color: subCategoryColor,
      children: flexibleLinks.subCategory.map((item, i) => getFobColumnGroupData(item, i, newIdPrefix, linkColor)),
    };
  });
}

function mapDesktopMenu(data) {
  if (!data.header?.fobs) return [];
  return data.header.fobs?.globalNavigation.reduce((acc, fob, index) => {
    const idPrefix = index.toString();
    const { contentlinks, attributes, flyout = {} } = fob;
    const mainLink = contentlinks?.[0];

    if (!mainLink) {
      return acc;
    }

    const { container } = flyout;
    const color = getColorFromAttributes(attributes, 'fontColor');
    let children;

    if (container) {
      children = container.map((item, i) => getFobColumnData(item, i, idPrefix));
    } else {
      children = [];
    }

    const mainLinkUrl = mainLink.semanticUrl || mainLink.value || '';

    if (!mainLinkUrl && !children.length) {
      return acc;
    }

    mainLink.id = mainLink.value || idPrefix;

    const category = {
      id: mainLink.id,
      tracking: mainLink.tracking,
      text: mainLink.text,
      url: mainLinkUrl,
      children,
      color,
    };

    acc.push(category);

    return acc;
  }, []);
}

function getMobileTopCategoryIds(properties) {
  const ids = [];

  properties?.menuLabels?.forEach((label) => {
    ids.push(...properties[label]);
  });

  return ids;
}

function parseMobileMenu(menu, topMenuIds) {
  const menuObj = menu || {};
  const topMenuIdsMap = topMenuIds.reduce((acc, id) => {
    acc[id] = true;
    return acc;
  }, {});
  return Object.values(menuObj).reduce((acc, { id, text, tracking, url, parent, children, categoryPageType, fontColor, isSyndicate }) => {
    if (topMenuIdsMap[id]) {
      if (!url && !children?.length) {
        return acc;
      }
    } else if (!children?.length) {
      return acc;
    }

    acc[id] = {
      id,
      text,
      tracking: tracking || null,
      url: url || '',
      ...(fontColor ? { fontColor } : {}),
    };

    const childrenItems =
      isSyndicate && url && parent?.id
        ? []
        : (children || []).reduce((childrenAcc, link) => {
            if (!link.url && !menuObj[link.id]?.children?.length) {
              return childrenAcc;
            }

            childrenAcc.push({
              id: link.id,
              text: link.text,
              url: link.url || '',
              tracking: link.tracking || null,
              isCatSplash: Boolean(link.categoryPageType === 'Category Splash' && menu[link.id]?.children?.length),
              ...(link.fontColor ? { fontColor: link.fontColor } : {}),
            });

            return childrenAcc;
          }, []);

    acc[id].children = childrenItems;
    acc[id].isCatSplash = Boolean(categoryPageType === 'Category Splash' && childrenItems.length);

    if (parent?.id) {
      acc[id].parentId = parent.id;
    }

    return acc;
  }, {});
}

function parseAndReduceMobileMenu(menu, properties) {
  const reducedMobileMenu = {};
  const categoryLevels = {};
  const topCategories = getMobileTopCategoryIds(properties);
  const mobileMenu = parseMobileMenu(menu || {}, topCategories);

  topCategories.forEach((id) => {
    const category = mobileMenu[id];

    if (!category) {
      return;
    }

    reducedMobileMenu[id] = category;
    (categoryLevels[id] || (categoryLevels[id] = {})).l1 = true;
    category?.children?.forEach(({ id: subCategoryId }) => {
      const subCategory = mobileMenu[subCategoryId];

      if (subCategory) {
        reducedMobileMenu[subCategoryId] = subCategory;
        (categoryLevels[subCategoryId] || (categoryLevels[subCategoryId] = {})).l2 = true;

        subCategory.children?.forEach(({ id: l3SubCategoryId, url: l3Url }) => {
          // save L3 categories to show L4 only if L3 is a browse-hide category
          // browse-hide category is a category without url property
          if (!l3Url) {
            const l3SubCategory = mobileMenu[l3SubCategoryId];

            if (l3SubCategory?.children?.length) {
              reducedMobileMenu[l3SubCategoryId] = l3SubCategory;
              (categoryLevels[l3SubCategoryId] || (categoryLevels[l3SubCategoryId] = {})).l3 = true;
            }
          }
        });
      }
    });
  });

  // remove redundant empty links from children
  Object.values(reducedMobileMenu).forEach((category) => {
    if (category?.children?.length) {
      category.children = category.children.filter(
        (link) =>
          link.url ||
          ((categoryLevels[link.id]?.l1 || categoryLevels[link.id]?.l2 || categoryLevels[link.id]?.l3) &&
            // this condition is needed to prevent hydration issues as we may receive categories like these:
            // {
            //  "id": "1242628",
            //  "text": "Fall Trends",
            //  "children": [
            //    { "group": [
            //      { "id": "1238674", "text": "Shop All" },
            //      { "id": "1238670", "text": "70's Sophistication" },
            //      { "id": "1238669", "text": "City Chic" },
            //      { "id": "1238671", "text": "Animal Print" }
            //  ],
            // }
            // in this example we don't have category url and children urls
            // we will reduce it to the next structure:
            // {
            //  "id": "1242628",
            //  "text": "Fall Trends",
            //  "children": []
            // }
            // so we need to filter out any references to the categories like this from another categories
            (reducedMobileMenu[link.id]?.url || reducedMobileMenu[link.id]?.children?.length)),
        [],
      );
    }
  });

  return reducedMobileMenu;
}

function fixMyAccountLinks(links, viewType) {
  const isDesktop = viewType === viewTypes.desktop;
  const normalizedLinks = links.map(({ value: url, text, tracking }) => ({ url, text, tracking }));

  return isDesktop ? remove(normalizedLinks, ['star rewards']) : normalizedLinks;
}

function mapHamburgerMenu(data) {
  const killswitches = data.header?.meta?.properties || {};
  const { menu = null } = data.header || {};

  if (!killswitches?.menuLabels?.length) {
    killswitches.menuLabels = mobileTitles?.shopByDepartment ? [mobileTitles.shopByDepartment] : [];

    if (mobileTitles?.shopByDepartment) {
      const { topMenu = '' } = killswitches;
      killswitches[mobileTitles.shopByDepartment] = topMenu.split(',');
    }
  }

  return parseAndReduceMobileMenu(menu, killswitches);
}

function buildHamburgerMenuTopGroups(data) {
  const killswitches = data.header?.meta?.properties || {};
  const { menuLabels = [] } = killswitches;
  const topGroupsMenu = menuLabels.reduce(
    (acc, key) =>
      acc.concat({
        title: key,
        // filter out empty static links to avoid hydration issues
        ids: killswitches[key].filter((id) => {
          const link = data.menu?.[id];
          return link && (link.url || link.children?.length);
        }),
      }),
    [],
  );

  return topGroupsMenu;
}

function normalizeHeaderData({ source, viewType, isMcom, killswitchesOverride }) {
  const data = { ...source };
  const isMobile = viewType === viewTypes.mobile;
  const accountLinks = remove(data.header?.signInLinks?.contentlinks || [], ['Sign In']);
  const killswitches = data.header?.meta?.properties;

  data.accountLinks = fixMyAccountLinks(accountLinks, viewType);

  if (isMcom) {
    data.skinnyDealsBanner = data.header?.skinnyDealsBanner;
  } else {
    data.topNavigation = data.header?.topNavigation?.map(({ text }) => ({
      text: text && transformEditorPopupLink(text),
    }));
  }
  data.meta = { ...data.header?.meta };
  data.secondaryNavigation = data.header?.secondaryNavigation?.contentlinks.map(({ text, value: url, tracking }) => ({ text, url, tracking })) || [];

  if (!isMobile) {
    data.desktopMenu = mapDesktopMenu(data);
  }
  data.menu = mapHamburgerMenu(data);

  const topGroupsMenu = buildHamburgerMenuTopGroups(data);
  const filteredKillswitches = combineKillswitches({ killswitches, localKillswitches, killswitchesOverride });

  data.meta.properties = { topMenu: killswitches?.topMenu, topGroupsMenu };

  delete data.meta.soasta_pageinfo;
  delete data.header;

  return { data, filteredKillswitches };
}

export {
  buildHeaderRequestUrl,
  loadHeaderMock,
  remove,
  normalizeHeaderData,
  getExperimentIdForMock,
  // for tests
  isExperimentActive,
  getColorFromAttributes,
  getFobColumnGroupData,
  getFobColumnData,
  mapDesktopMenu,
  getMobileTopCategoryIds,
  parseMobileMenu,
  parseAndReduceMobileMenu,
  fixMyAccountLinks,
};
